"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const bus_http_1 = require("@comunica/bus-http");
const bus_rdf_dereference_1 = require("@comunica/bus-rdf-dereference");
const relative_to_absolute_iri_1 = require("relative-to-absolute-iri");
/**
 * An actor that listens on the 'rdf-dereference' bus.
 *
 * It starts by grabbing all available RDF media types from the RDF parse bus.
 * After that, it resolves the URL using the HTTP bus using an accept header compiled from the available media types.
 * Finally, the response is parsed using the RDF parse bus.
 */
class ActorRdfDereferenceHttpParseBase extends bus_rdf_dereference_1.ActorRdfDereferenceMediaMappings {
    constructor(args) {
        super(args);
    }
    async test(action) {
        if (!/^https?:/.test(action.url)) {
            throw new Error(`Cannot retrieve ${action.url} because it is not an HTTP(S) URL.`);
        }
        return true;
    }
    async run(action) {
        // Define accept header based on available media types.
        const mediaTypes = (await this.mediatorRdfParseMediatypes.mediate({ context: action.context, mediaTypes: true }))
            .mediaTypes;
        const acceptHeader = this.mediaTypesToAcceptString(mediaTypes, this.getMaxAcceptHeaderLength());
        // Resolve HTTP URL using appropriate accept header
        const headers = new Headers();
        headers.append('Accept', acceptHeader);
        // Append any custom passed headers
        for (const key in action.headers) {
            headers.append(key, action.headers[key]);
        }
        const httpAction = {
            context: action.context,
            init: { headers, method: action.method },
            input: action.url,
        };
        let httpResponse;
        try {
            httpResponse = await this.mediatorHttp.mediate(httpAction);
        }
        catch (error) {
            return this.handleDereferenceError(action, error);
        }
        const url = relative_to_absolute_iri_1.resolve(httpResponse.url, action.url); // The response URL can be relative to the given URL
        // Convert output headers to a hash
        const outputHeaders = {};
        httpResponse.headers.forEach((value, key) => outputHeaders[key] = value);
        // Wrap WhatWG readable stream into a Node.js readable stream
        // If the body already is a Node.js stream (in the case of node-fetch), don't do explicit conversion.
        const responseStream = bus_http_1.ActorHttp.toNodeReadable(httpResponse.body);
        // Only parse if retrieval was successful
        if (httpResponse.status !== 200) {
            const error = new Error(`Could not retrieve ${action.url} (${httpResponse.status}: ${httpResponse.statusText || 'unknown error'})`);
            return this.handleDereferenceError(action, error);
        }
        // Parse the resulting response
        let mediaType = httpResponse.headers.has('content-type')
            ? ActorRdfDereferenceHttpParseBase.REGEX_MEDIATYPE.exec(httpResponse.headers.get('content-type'))[0] : null;
        // If no media type could be found, try to determine it via the file extension
        if (!mediaType || mediaType === 'text/plain') {
            mediaType = this.getMediaTypeFromExtension(httpResponse.url);
        }
        const parseAction = {
            baseIRI: url,
            headers: httpResponse.headers,
            input: responseStream,
        };
        let parseOutput;
        try {
            parseOutput = (await this.mediatorRdfParseHandle.mediate({ context: action.context, handle: parseAction, handleMediaType: mediaType })).handle;
        }
        catch (error) {
            return this.handleDereferenceError(action, error);
        }
        const quads = this.handleDereferenceStreamErrors(action, parseOutput.quads);
        // Return the parsed quad stream and whether or not only triples are supported
        return { url, quads, triples: parseOutput.triples, headers: outputHeaders };
    }
    mediaTypesToAcceptString(mediaTypes, maxLength) {
        maxLength -= 10; // Ensure a ',*/*;q=0.1' suffix
        const parts = [];
        const sortedMediaTypes = Object.keys(mediaTypes)
            .map((mediaType) => ({ mediaType, priority: mediaTypes[mediaType] }))
            .sort((a, b) => b.priority - a.priority);
        let partsLength = 0;
        for (const entry of sortedMediaTypes) {
            const part = entry.mediaType + (entry.priority !== 1 ? ';q=' + entry.priority.toFixed(3).replace(/0*$/, '') : '');
            if (partsLength + part.length > maxLength) {
                parts.push('*/*;q=0.1');
                break;
            }
            parts.push(part);
            partsLength += part.length;
        }
        if (!parts.length) {
            return '*/*';
        }
        return parts.join(',');
    }
}
exports.ActorRdfDereferenceHttpParseBase = ActorRdfDereferenceHttpParseBase;
ActorRdfDereferenceHttpParseBase.REGEX_MEDIATYPE = /^[^ ;]*/;
//# sourceMappingURL=ActorRdfDereferenceHttpParseBase.js.map